// src/hooks/useConveChat.ts

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { conveRoomApi } from '../api/conveApi';
import { ConveChatRoom } from '../types/conve';

export const useConveChat = () => {
  const navigate = useNavigate();

  const [rooms, setRooms]               = useState<ConveChatRoom[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [activeRoomId, setActiveRoomId] = useState<number | null>(null);

  // 대화방 목록 로딩
  const loadRooms = useCallback(async () => {
    setLoadingRooms(true);
    try {
      const res = await conveRoomApi.getRooms();
      setRooms(res.data.data || []);
    } catch {
      setRooms([]);
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  // 새 대화방 생성 → 해당 채팅방으로 이동
  const createRoom = useCallback(async (model: string) => {
    const res = await conveRoomApi.createRoom(model);
    const newRoom: ConveChatRoom = res.data.data;
    setRooms(prev => [newRoom, ...prev]);
    setActiveRoomId(newRoom.id);
    navigate(`/conve/chat/${newRoom.id}`);
    return newRoom;
  }, [navigate]);

  // 제목 수정
  const renameRoom = useCallback(async (roomId: number, title: string) => {
    await conveRoomApi.renameRoom(roomId, title);
    setRooms(prev =>
      prev.map(r => r.id === roomId ? { ...r, title } : r)
    );
  }, []);

  // 대화방 삭제
  const deleteRoom = useCallback(async (roomId: number) => {
    await conveRoomApi.deleteRoom(roomId);
    setRooms(prev => prev.filter(r => r.id !== roomId));
    if (activeRoomId === roomId) {
      setActiveRoomId(null);
      navigate('/conve');
    }
  }, [activeRoomId, navigate]);

  // 대화방 선택
  const selectRoom = useCallback((roomId: number) => {
    setActiveRoomId(roomId);
    navigate(`/conve/chat/${roomId}`);
  }, [navigate]);

  return {
    rooms,
    loadingRooms,
    activeRoomId,
    setActiveRoomId,
    loadRooms,
    createRoom,
    renameRoom,
    deleteRoom,
    selectRoom,
  };
};
